import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Open Dental: 6 sites per tooth — MB, B, DB on buccal; ML, L, DL on lingual.
const SITES = ["MB", "B", "DB", "ML", "L", "DL"];
const UPPER_RIGHT = ["18","17","16","15","14","13","12","11"];
const UPPER_LEFT  = ["21","22","23","24","25","26","27","28"];
const LOWER_RIGHT = ["48","47","46","45","44","43","42","41"];
const LOWER_LEFT  = ["31","32","33","34","35","36","37","38"];
const ALL_TEETH = [...UPPER_RIGHT, ...UPPER_LEFT, ...LOWER_RIGHT, ...LOWER_LEFT];

const newEmptyTooth = () => ({
  sites: Object.fromEntries(SITES.map((s) => [s, { depth: null, bleeding: false, suppuration: false, recession: null }])),
  mobility: 0, furcation: 0, plaque: false, notes: "",
});

const depthColor = (d) => {
  if (d == null) return "";
  if (d >= 6) return "bg-[#9E3C3C] text-white";
  if (d >= 4) return "bg-[#D4A24C]/40 text-[#8B6914]";
  return "";
};

export default function PerioChart({ patientId }) {
  const [exams, setExams] = useState([]);
  const [editing, setEditing] = useState(null); // active exam object or null
  const [creating, setCreating] = useState(false);
  const [arch, setArch] = useState("upper"); // upper | lower

  const load = async () => {
    const { data } = await api.get(`/perio/${patientId}`);
    setExams(data);
    if (data.length > 0 && !editing) setEditing(data[0]);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const startNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    const teethData = Object.fromEntries(ALL_TEETH.map((t) => [t, newEmptyTooth()]));
    setEditing({
      id: null, exam_date: today, notes: "", teeth: teethData,
    });
    setCreating(true);
  };

  const updateSite = (toothNum, site, field, value) => {
    setEditing((prev) => {
      const t = { ...(prev.teeth[toothNum] || newEmptyTooth()) };
      const s = { ...t.sites[site] };
      s[field] = field === "depth" || field === "recession"
        ? (value === "" ? null : Number(value))
        : Boolean(value);
      t.sites = { ...t.sites, [site]: s };
      return { ...prev, teeth: { ...prev.teeth, [toothNum]: t } };
    });
  };
  const updateTooth = (toothNum, field, value) => {
    setEditing((prev) => {
      const t = { ...(prev.teeth[toothNum] || newEmptyTooth()), [field]: value };
      return { ...prev, teeth: { ...prev.teeth, [toothNum]: t } };
    });
  };

  const save = async () => {
    try {
      const body = {
        exam_date: editing.exam_date,
        notes: editing.notes || "",
        teeth: editing.teeth,
      };
      if (editing.id) {
        await api.put(`/perio/${editing.id}`, body);
        toast.success("Ekzaminimi u përditësua");
      } else {
        const { data } = await api.post(`/perio/${patientId}`, body);
        toast.success("Ekzaminimi u krijua");
        setEditing(data);
      }
      setCreating(false);
      load();
    } catch (err) {
      toast.error("Gabim gjatë ruajtjes");
    }
  };

  const removeExam = async (id) => {
    if (!window.confirm("Fshini këtë ekzaminim?")) return;
    await api.delete(`/perio/${id}`);
    setEditing(null);
    toast.success("U fshi");
    load();
  };

  const teethRow = arch === "upper" ? [...UPPER_RIGHT, ...UPPER_LEFT] : [...LOWER_RIGHT, ...LOWER_LEFT];

  // Statistics for current exam
  const stats = useMemo(() => {
    if (!editing?.teeth) return { avgDepth: 0, bleedingPct: 0, deep: 0, total: 0 };
    let sumDepth = 0, depthCount = 0, bleeding = 0, total = 0, deep = 0;
    Object.values(editing.teeth).forEach((t) => {
      SITES.forEach((s) => {
        const site = t.sites?.[s] || {};
        if (site.depth != null) { sumDepth += site.depth; depthCount++; total++; if (site.depth >= 4) deep++; }
        if (site.bleeding) bleeding++;
      });
    });
    return {
      avgDepth: depthCount ? (sumDepth / depthCount).toFixed(1) : "0",
      bleedingPct: total ? Math.round((bleeding / total) * 100) : 0,
      deep,
      total,
    };
  }, [editing]);

  return (
    <div className="space-y-6" data-testid="perio-chart">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Periodontologji</div>
          <h3 className="mt-1 text-2xl font-semibold tracking-tight">Periodontal Chart</h3>
          <p className="mt-1 text-sm text-[#5C5C5C]">6-pikësh probe depths (MB·B·DB·ML·L·DL), gjakderdhje, recesion dhe lëvizshmëri.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={startNew} className="btn-primary" data-testid="new-perio-button">
            <Plus size={16} /> Ekzaminim i Ri
          </button>
        </div>
      </div>

      {exams.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-eyebrow">Ekzaminimet:</span>
          {exams.map((e) => (
            <button key={e.id} onClick={() => { setEditing(e); setCreating(false); }}
                    className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                      editing?.id === e.id ? "border-[#2C4C3B] bg-[#2C4C3B] text-[#FDFBF7]" : "border-[#E2DFD8] hover:border-[#2C4C3B]"
                    }`}>
              {new Date(e.exam_date).toLocaleDateString("sq-AL", { dateStyle: "medium" })}
            </button>
          ))}
        </div>
      )}

      {!editing ? (
        <div className="surface-card p-10 text-center text-sm text-[#5C5C5C]">
          Pa ekzaminime periodontale. Klikoni "Ekzaminim i Ri" për të filluar.
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatBox label="Thellësia Mes." value={`${stats.avgDepth} mm`} />
            <StatBox label="BOP %" value={`${stats.bleedingPct}%`} accent={stats.bleedingPct > 20} />
            <StatBox label="Xhepa ≥4mm" value={stats.deep} accent={stats.deep > 0} />
            <StatBox label="Sipërfaqe Totale" value={stats.total} />
          </div>

          <div className="surface-card p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="label-eyebrow">Data e Ekzaminimit</Label>
                <Input type="date" value={editing.exam_date}
                       onChange={(e) => setEditing({ ...editing, exam_date: e.target.value })}
                       className="mt-2" data-testid="perio-date" />
              </div>
              <div>
                <Label className="label-eyebrow">Shënime të Përgjithshme</Label>
                <Input value={editing.notes || ""}
                       onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                       className="mt-2" placeholder="P.sh. Gingivit gjeneralizuar" />
              </div>
            </div>

            <div className="inline-flex rounded-full border border-[#E2DFD8] bg-white p-1 mb-4">
              <ArchBtn label="Maxilla (Lart)" active={arch === "upper"} onClick={() => setArch("upper")} testId="arch-upper" />
              <ArchBtn label="Mandibula (Poshtë)" active={arch === "lower"} onClick={() => setArch("lower")} testId="arch-lower" />
            </div>

            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-right label-eyebrow w-24">Dhëmbi →</th>
                    {teethRow.map((t) => (
                      <th key={t} className="px-1 py-1 text-center font-mono text-[10px] text-[#5C5C5C] border-l border-[#E2DFD8] min-w-[42px]">
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Buccal sites: MB, B, DB */}
                  <tr className="bg-[#FDFBF7]">
                    <td colSpan={teethRow.length + 1} className="px-2 py-1 label-eyebrow text-[10px]">Bukal (MB · B · DB)</td>
                  </tr>
                  {["MB", "B", "DB"].map((site) => (
                    <SiteRow key={`b-${site}`} site={site} teeth={teethRow} editing={editing}
                             updateSite={updateSite} />
                  ))}
                  {/* Lingual sites */}
                  <tr className="bg-[#FDFBF7]">
                    <td colSpan={teethRow.length + 1} className="px-2 py-1 label-eyebrow text-[10px]">Lingual (ML · L · DL)</td>
                  </tr>
                  {["ML", "L", "DL"].map((site) => (
                    <SiteRow key={`l-${site}`} site={site} teeth={teethRow} editing={editing}
                             updateSite={updateSite} />
                  ))}
                  {/* Bleeding */}
                  <tr className="bg-[#FDFBF7]">
                    <td colSpan={teethRow.length + 1} className="px-2 py-1 label-eyebrow text-[10px]">Indikator</td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1 text-right text-[10px] text-[#5C5C5C]">Lëvizshmëria (0-3)</td>
                    {teethRow.map((t) => (
                      <td key={t} className="border-l border-t border-[#E2DFD8] p-0">
                        <input type="number" min="0" max="3"
                               value={editing.teeth?.[t]?.mobility ?? 0}
                               onChange={(e) => updateTooth(t, "mobility", Number(e.target.value))}
                               className="w-full h-7 text-center text-xs border-0 focus:bg-[#EAE4D9]/40" />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-2 py-1 text-right text-[10px] text-[#5C5C5C]">Furkacion (0-3)</td>
                    {teethRow.map((t) => (
                      <td key={t} className="border-l border-t border-[#E2DFD8] p-0">
                        <input type="number" min="0" max="3"
                               value={editing.teeth?.[t]?.furcation ?? 0}
                               onChange={(e) => updateTooth(t, "furcation", Number(e.target.value))}
                               className="w-full h-7 text-center text-xs border-0 focus:bg-[#EAE4D9]/40" />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex justify-between items-center flex-wrap gap-3">
              <div className="text-xs text-[#5C5C5C]">
                <span className="inline-block w-3 h-3 bg-[#D4A24C]/40 rounded mr-1"></span> 4-5mm
                <span className="inline-block w-3 h-3 bg-[#9E3C3C] rounded ml-3 mr-1"></span> ≥6mm
                <span className="inline-block w-3 h-3 bg-[#9E3C3C] rounded-full ml-3 mr-1"></span> BOP
              </div>
              <div className="flex gap-2">
                {editing.id && (
                  <button onClick={() => removeExam(editing.id)} className="btn-ghost text-[#9E3C3C]">
                    <Trash2 size={14} /> Fshi
                  </button>
                )}
                <button onClick={save} className="btn-primary" data-testid="save-perio-button">
                  {creating ? "Ruaj Ekzaminimin" : "Përditëso"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SiteRow({ site, teeth, editing, updateSite }) {
  return (
    <>
      <tr>
        <td className="px-2 py-1 text-right text-[10px] text-[#5C5C5C] font-mono">{site} · mm</td>
        {teeth.map((t) => {
          const s = editing.teeth?.[t]?.sites?.[site] || {};
          return (
            <td key={t} className={`border-l border-t border-[#E2DFD8] p-0 ${depthColor(s.depth)}`}>
              <input type="number" min="0" max="12"
                     value={s.depth ?? ""}
                     onChange={(e) => updateSite(t, site, "depth", e.target.value)}
                     className={`w-full h-7 text-center text-xs border-0 bg-transparent focus:bg-[#EAE4D9]/40`}
                     data-testid={`depth-${t}-${site}`} />
            </td>
          );
        })}
      </tr>
      <tr>
        <td className="px-2 py-1 text-right text-[9px] text-[#5C5C5C]">BOP</td>
        {teeth.map((t) => {
          const s = editing.teeth?.[t]?.sites?.[site] || {};
          return (
            <td key={t} className="border-l border-t border-[#E2DFD8] p-0 text-center">
              <button onClick={() => updateSite(t, site, "bleeding", !s.bleeding)}
                      className={`w-full h-5 ${s.bleeding ? "bg-[#9E3C3C]" : "hover:bg-[#EAE4D9]/40"}`}
                      title="Bleeding on probing">
                {s.bleeding && <span className="text-white text-[10px]">●</span>}
              </button>
            </td>
          );
        })}
      </tr>
    </>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`surface-card p-4 ${accent ? "border-[#9E3C3C]/40" : ""}`}>
      <div className="label-eyebrow">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tracking-tight ${accent ? "text-[#9E3C3C]" : ""}`}>{value}</div>
    </div>
  );
}

function ArchBtn({ label, active, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              active ? "bg-[#2C4C3B] text-[#FDFBF7]" : "text-[#5C5C5C] hover:text-[#2C4C3B]"
            }`}>{label}</button>
  );
}
